// src/components/notification-center/ctl-notification-center.js
//
// Notification Center (panel + trigger) for enterprise apps.
// - Works standalone (no router assumptions)
// - Supports push-style usage: notifications property / addNotification() method

import { safeJsonParse } from '../../../utils/safeJsonParse.js';
//
// Attributes
// - open (boolean)            : panel open/closed
// - placement (string)        : "bottom-end" | "bottom-start" | "top-end" | "top-start" (default "bottom-end")
// - max (number)              : max items to show (default 25)
// - unread (number)           : read-only-ish convenience (reflected when notifications change)
//
// Properties
// - notifications (array)     : [{ id, title, message?, time?, tone?, href?, read?, actions? }]
//    - tone: "neutral"|"info"|"success"|"warning"|"danger"
//    - actions: [{ id, label, tone? }]
//
// Methods
// - addNotification(n)        : prepends a notification (auto id if missing)
// - markRead(id, read=true)
// - clearAll()
//
// Events
// - ctl-open-change (detail: { open })
// - ctl-action      (detail: { id, actionId, notification })
// - ctl-navigate    (detail: { id, href, notification })   (when a notification has href and user clicks it)
// - ctl-clear       (detail: { clearedCount })
// - ctl-mark-read   (detail: { id, read })
//
// Slots
// - trigger  : custom trigger button/icon (optional). If not provided, a default bell trigger is rendered.
// - header   : override header content (optional)
// - empty    : empty state content (optional)
//
// Notes
// - Click outside closes.
// - ESC closes.
// - Focus is moved into the panel on open, back to trigger on close.
// - Keyboard: Tab works normally; ArrowUp/Down roves within list; Enter activates focused item.

import "../foundation/ctl-button.js";
import "../foundation/ctl-badge.js";
import "../data-display/ctl-tag.js";
import "../foundation/ctl-divider.js";
import "../foundation/ctl-typography.js";
import "../foundation/ctl-icon.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --ctl-text: var(--ctl-color-text);
    --ctl-muted: var(--ctl-color-muted);
    --ctl-bg: var(--ctl-color-bg);
    --ctl-surface: var(--ctl-color-surface);
    --ctl-surface-2: var(--ctl-color-surface-2);
    --ctl-border: var(--ctl-color-border);
    --ctl-brand: var(--ctl-color-brand);
    --ctl-focus: var(--ctl-focus-ring, 0 0 0 3px rgba(79,70,229,.15));

    display:inline-block;
    font-family: var(--ctl-font);
    color: var(--ctl-text);
    position: relative;
  }

  .triggerWrap{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    position:relative;
  }

  .defaultTrigger{
    all: unset;
    cursor: pointer;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    width: var(--ctl-space-9-5);
    height: var(--ctl-space-9-5);
    border-radius: var(--ctl-radius-md);
    border: var(--ctl-space-0-5) solid color-mix(in srgb, var(--ctl-border) var(--ctl-opacity-hover), transparent);
    background: var(--ctl-surface);
    box-shadow: var(--ctl-shadow-sm, 0 var(--ctl-space-1-5) var(--ctl-space-4-5) var(--ctl-color-overlay-5));
  }
  .defaultTrigger:hover{
    background: color-mix(in srgb, var(--ctl-brand) var(--ctl-opacity-subtle), var(--ctl-surface));
  }
  .defaultTrigger:focus-visible{
    outline:none;
    box-shadow: var(--ctl-focus);
  }

  .bell{
    width: var(--ctl-space-4-5);
    height: var(--ctl-space-4-5);
    color: var(--ctl-muted);
  }

  .dot{
    position:absolute;
    top: var(--ctl-space-1-5);
    right: var(--ctl-space-1-5);
    min-width: var(--ctl-space-4-5);
    height: var(--ctl-space-4-5);
    border-radius: var(--ctl-radius-full);
    background: var(--ctl-brand);
    color: var(--ctl-color-white, white);
    display:none;
    align-items:center;
    justify-content:center;
    font-size: var(--ctl-font-size-xs);
    font-weight: var(--ctl-font-weight-black);
    padding: 0 var(--ctl-space-1-5);
    border: var(--ctl-space-0-5) solid var(--ctl-surface);
    box-sizing: border-box;
  }
  :host([data-unread="0"]) .dot{ display:none; }
  :host(:not([data-unread="0"])) .dot{ display:flex; }

  /* Panel */
  .panel{
    position: fixed; /* anchored via JS */
    z-index: var(--ctl-z-overlay, 1100);
    width: min(var(--ctl-space-105), calc(100vw - var(--ctl-space-6)));
    max-height: min(var(--ctl-space-135), calc(100vh - var(--ctl-space-6)));
    background: var(--ctl-surface);
    border: var(--ctl-space-0-5) solid var(--ctl-border);
    border-radius: var(--ctl-radius-lg, var(--ctl-radius-lg));
    box-shadow: var(--ctl-shadow-lg, 0 var(--ctl-space-4) var(--ctl-space-10) var(--ctl-color-overlay-6));
    overflow: hidden;
    display:none;
  }
  :host([open]) .panel{ display:block; }

  .panelHead{
    padding: var(--ctl-space-3) var(--ctl-space-3);
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap: var(--ctl-space-2-5);
    border-bottom: var(--ctl-space-0-5) solid var(--ctl-border);
    background: var(--ctl-surface);
  }

  .title{
    display:flex;
    align-items:baseline;
    gap: var(--ctl-space-2-5);
    min-width: 0;
  }
  .title strong{
    font-weight: var(--ctl-font-weight-black);
    letter-spacing: var(--ctl-letter-spacing-tight);
  }
  .sub{
    color: var(--ctl-muted);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-bold);
    white-space: nowrap;
  }

  .headActions{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    flex: 0 0 auto;
  }

  .body{
    background: var(--ctl-bg);
    overflow:auto;
    max-height: inherit;
  }

  .list{
    list-style:none;
    padding: var(--ctl-space-2);
    margin: 0;
    display:grid;
    gap: var(--ctl-space-2);
  }

  .item{
    border: var(--ctl-space-0-5) solid color-mix(in srgb, var(--ctl-border) var(--ctl-opacity-hover), transparent);
    border-radius: var(--ctl-radius-md);
    background: var(--ctl-surface);
    padding: var(--ctl-space-2-5) var(--ctl-space-2-5);
    display:grid;
    gap: var(--ctl-space-1-5);
    cursor: pointer;
    outline: none;
  }
  .item:hover{
    background: color-mix(in srgb, var(--ctl-brand) var(--ctl-opacity-subtle), var(--ctl-surface));
  }
  .item:focus-visible{
    box-shadow: var(--ctl-focus);
  }

  .itemTop{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap: var(--ctl-space-2-5);
    min-width: 0;
  }

  .itemTitle{
    font-weight: var(--ctl-font-weight-black);
    letter-spacing: var(--ctl-letter-spacing-tight);
    line-height: var(--ctl-line-height-tight);
    min-width: 0;
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .meta{
    color: var(--ctl-muted);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-bold);
    white-space: nowrap;
    flex: 0 0 auto;
  }

  .itemMsg{
    color: var(--ctl-muted);
    font-size: var(--ctl-font-size-md);
    line-height: var(--ctl-line-height-normal);
  }

  .row{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap: var(--ctl-space-2-5);
  }

  .pill{
    display:inline-flex;
    align-items:center;
    gap: var(--ctl-space-2);
    flex-wrap: wrap;
  }

  .unreadMark{
    width: var(--ctl-space-2);
    height: var(--ctl-space-2);
    border-radius: var(--ctl-radius-full);
    background: var(--ctl-brand);
    flex: 0 0 auto;
    margin-top: var(--ctl-space-1);
  }

  .item[aria-selected="true"]{
    border-color: color-mix(in srgb, var(--ctl-brand) var(--ctl-opacity-medium), var(--ctl-border));
    background: color-mix(in srgb, var(--ctl-brand) var(--ctl-opacity-hover-subtle), var(--ctl-surface));
  }

  .empty{
    padding: var(--ctl-space-3-5);
    color: var(--ctl-muted);
    font-size: var(--ctl-font-size-md);
    text-align:center;
  }

  /* Close button (x) */
  .x{
    all: unset;
    cursor:pointer;
    width: var(--ctl-space-6);
    height: var(--ctl-space-6);
    border-radius: var(--ctl-radius-lg);
    display:grid;
    place-items:center;
    color: var(--ctl-muted);
  }
  .x:hover{ background: color-mix(in srgb, var(--ctl-border) var(--ctl-opacity-medium), transparent); }
  .x:focus-visible{ outline:none; box-shadow: var(--ctl-focus); }

  /* Hide light DOM templates used for overrides */
  ::slotted([slot="trigger"]),
  ::slotted([slot="header"]),
  ::slotted([slot="empty"]){
    /* visible, do not hide */
  }
</style>

<div class="triggerWrap" part="trigger">
  <slot name="trigger" id="triggerSlot">
    <button class="defaultTrigger" id="defaultTrigger" type="button" aria-haspopup="dialog" aria-expanded="false" aria-label="Open notifications">
      <span class="bell" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M15 17H9" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
          <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 7h18s-3 0-3-7Z"
                stroke="currentColor" stroke-width="2.2" stroke-linejoin="round"/>
        </svg>
      </span>
    </button>
  </slot>
  <div class="dot" id="dot" aria-hidden="true"></div>
</div>

<div class="panel" id="panel" role="dialog" aria-modal="false" aria-label="Notifications" part="panel">
  <div class="panelHead" part="header">
    <slot name="header">
      <div class="title">
        <strong>Notifications</strong>
        <span class="sub" id="sub"></span>
      </div>
    </slot>

    <div class="headActions">
      <ctl-button size="sm" variant="secondary" id="btnMarkAll">Mark all read</ctl-button>
      <ctl-button size="sm" variant="secondary" id="btnClear">Clear</ctl-button>
      <button class="x" id="btnClose" type="button" aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" width="16" height="16" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
        </svg>
      </button>
    </div>
  </div>

  <div class="body" part="body">
    <div id="empty" class="empty" part="empty" hidden>
      <slot name="empty">You’re all caught up.</slot>
    </div>
    <ul class="list" id="list" part="list" role="listbox" aria-label="Notification list"></ul>
  </div>
</div>
`;

function toArray(v){ return Array.isArray(v) ? v : []; }
function uid(){ return `n_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`; }
function clampInt(v, d){
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}
function esc(s){
  return String(s ?? "")
    .replaceAll("&","&amp;").replaceAll("<","&lt;").replaceAll(">","&gt;")
    .replaceAll('"',"&quot;").replaceAll("'","&#039;");
}

function normalizeTone(t){
  const v = String(t || "neutral");
  return ["neutral","info","success","warning","danger"].includes(v) ? v : "neutral";
}

function normalizePlacement(p){
  const v = String(p || "bottom-end");
  return ["bottom-end","bottom-start","top-end","top-start"].includes(v) ? v : "bottom-end";
}

export class CtlNotificationCenter extends HTMLElement {
  static get observedAttributes() {
    return ["open","placement","max","unread","disabled"];
  }

  constructor(){
    super();
    this.attachShadow({ mode:"open" }).appendChild(template.content.cloneNode(true));

    this._panel = this.shadowRoot.getElementById("panel");
    this._list = this.shadowRoot.getElementById("list");
    this._empty = this.shadowRoot.getElementById("empty");
    this._sub = this.shadowRoot.getElementById("sub");

    this._triggerSlot = this.shadowRoot.getElementById("triggerSlot");
    this._defaultTrigger = this.shadowRoot.getElementById("defaultTrigger");
    this._dot = this.shadowRoot.getElementById("dot");

    this._btnClose = this.shadowRoot.getElementById("btnClose");
    this._btnClear = this.shadowRoot.getElementById("btnClear");
    this._btnMarkAll = this.shadowRoot.getElementById("btnMarkAll");

    this._notifications = [];
    this._focusedIndex = -1;

    this._onDocPointerDown = this._onDocPointerDown.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onListClick = this._onListClick.bind(this);
    this._onActionClick = this._onActionClick.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onBtnCloseClick = this._onBtnCloseClick.bind(this);
    this._onBtnClearClick = this._onBtnClearClick.bind(this);
    this._onBtnMarkAllClick = this._onBtnMarkAllClick.bind(this);
  }

  connectedCallback(){
    this._wire();
    this._hydrateAttrs();
    this._render();
    this._syncOpen();
  }

  disconnectedCallback(){
    this._unwire();
  }

  attributeChangedCallback(name){
    if (name === "open") this._syncOpen();
    if (name === "placement" || name === "max") this._render();
  }

  // ----- API -----
  get open(){ return this.hasAttribute("open"); }
  set open(v){
    v ? this.setAttribute("open","") : this.removeAttribute("open");
  }

  get placement(){ return normalizePlacement(this.getAttribute("placement")); }
  set placement(v){ this.setAttribute("placement", normalizePlacement(v)); }

  get max(){ return clampInt(this.getAttribute("max"), 25); }
  set max(v){ this.setAttribute("max", String(clampInt(v, 25))); }

  get notifications(){ return this._notifications; }
  set notifications(v){
    const arr = toArray(v).map((n) => this._normalizeNotification(n)).filter((n) => n.id);
    this._notifications = arr;
    this._render();
  }

  addNotification(n){
    const nn = this._normalizeNotification(n);
    this._notifications = [nn, ...this._notifications];
    this._render();
  }

  markRead(id, read = true){
    const sid = String(id || "");
    let changed = false;
    this._notifications = this._notifications.map((n) => {
      if (n.id !== sid) return n;
      changed = true;
      return { ...n, read: !!read };
    });
    if (changed) {
      this._render();
      this.dispatchEvent(new CustomEvent("ctl-mark-read", {
        bubbles: true,
        composed: true,
        detail: { id: sid, read: !!read }
      }));
    }
  }

  clearAll(){
    const count = this._notifications.length;
    this._notifications = [];
    this._render();
    this.dispatchEvent(new CustomEvent("ctl-clear", {
      bubbles: true,
      composed: true,
      detail: { clearedCount: count }
    }));
  }

  // ----- internals -----
  _normalizeNotification(n){
    const obj = (n && typeof n === "object") ? n : {};
    const id = String(obj.id || uid());
    return {
      id,
      title: String(obj.title || "Notification"),
      message: obj.message == null ? "" : String(obj.message),
      time: obj.time == null ? "" : String(obj.time),
      tone: normalizeTone(obj.tone),
      href: obj.href == null ? "" : String(obj.href),
      read: !!obj.read,
      actions: toArray(obj.actions).map((a) => ({
        id: String(a?.id || ""),
        label: String(a?.label || ""),
        tone: normalizeTone(a?.tone || "neutral")
      })).filter((a) => a.id && a.label)
    };
  }

  _hydrateAttrs(){
    if (!this.hasAttribute("placement")) this.setAttribute("placement", "bottom-end");
    if (!this.hasAttribute("max")) this.setAttribute("max", "25");
  }

  _wire(){
    // trigger: either default trigger or slotted trigger
    this._defaultTrigger?.addEventListener("click", this._onTriggerClick);
    this._triggerSlot?.addEventListener("click", this._onTriggerClick);

    this._btnClose?.addEventListener("click", this._onBtnCloseClick);
    this._btnClear?.addEventListener("click", this._onBtnClearClick);
    this._btnMarkAll?.addEventListener("click", this._onBtnMarkAllClick);

    this._list.addEventListener("click", this._onListClick);

    window.addEventListener("resize", this._onResize);
    window.addEventListener("scroll", this._onResize, true);
  }

  _unwire(){
    this._defaultTrigger?.removeEventListener("click", this._onTriggerClick);
    this._triggerSlot?.removeEventListener("click", this._onTriggerClick);
    this._btnClose?.removeEventListener("click", this._onBtnCloseClick);
    this._btnClear?.removeEventListener("click", this._onBtnClearClick);
    this._btnMarkAll?.removeEventListener("click", this._onBtnMarkAllClick);
    this._list.removeEventListener("click", this._onListClick);

    window.removeEventListener("resize", this._onResize);
    window.removeEventListener("scroll", this._onResize, true);

    document.removeEventListener("pointerdown", this._onDocPointerDown, true);
    document.removeEventListener("keydown", this._onKeyDown, true);
  }

  _getTriggerEl(){
    // If user provided a slotted trigger, click listener is on slot; but we want the actual assigned element.
    const assigned = this._triggerSlot?.assignedElements?.({ flatten:true }) || [];
    if (assigned.length) return assigned[0];
    return this._defaultTrigger;
  }

  _syncOpen(){
    const open = this.open;

    // aria-expanded on default trigger (best-effort)
    const t = this._defaultTrigger;
    if (t) t.setAttribute("aria-expanded", open ? "true" : "false");

    if (open) {
      this._positionPanel();
      document.addEventListener("pointerdown", this._onDocPointerDown, true);
      document.addEventListener("keydown", this._onKeyDown, true);

      queueMicrotask(() => {
        // focus first item or close button
        const first = this.shadowRoot.querySelector('.item');
        if (first) {
          first.focus();
        } else if (this._btnClose) {
          this._btnClose.focus();
        }
      });
    } else {
      document.removeEventListener("pointerdown", this._onDocPointerDown, true);
      document.removeEventListener("keydown", this._onKeyDown, true);

      // return focus to trigger
      queueMicrotask(() => this._getTriggerEl()?.focus?.());
    }

    this.dispatchEvent(new CustomEvent("ctl-open-change", {
      bubbles: true,
      composed: true,
      detail: { open }
    }));
  }

  _open(){
    if (!this.open) this.open = true;
  }

  _close(){
    if (this.open) this.open = false;
  }

  _toggle(){
    this.open ? this._close() : this._open();
  }

  _onTriggerClick(e){
    // Prevent click on trigger from immediately closing via document pointerdown
    e.preventDefault();
    e.stopPropagation();
    this._toggle();
  }

  _onDocPointerDown(e){
    if (!this.open) return;
    const path = e.composedPath ? e.composedPath() : [];
    if (path.includes(this) || path.includes(this._panel)) return;
    this._close();
  }

  _onKeyDown(e){
    if (!this.open) return;

    if (e.key === "Escape") {
      e.preventDefault();
      this._close();
      return;
    }

    // roving focus within items
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      const items = Array.from(this.shadowRoot.querySelectorAll(".item"));
      if (!items.length) return;
      e.preventDefault();

      const active = this.getRootNode().activeElement;
      let idx = items.indexOf(active);
      if (idx < 0) idx = 0;

      const next = e.key === "ArrowDown"
        ? Math.min(items.length - 1, idx + 1)
        : Math.max(0, idx - 1);

      items[next].focus();
      return;
    }

    if (e.key === "Home" || e.key === "End") {
      const items = Array.from(this.shadowRoot.querySelectorAll(".item"));
      if (!items.length) return;
      e.preventDefault();
      (e.key === "Home" ? items[0] : items[items.length - 1]).focus();
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      const active = this.getRootNode().activeElement;
      if (active && active.classList?.contains("item")) {
        e.preventDefault();
        active.click();
      }
    }
  }

  _onResize(){
    if (this.open) this._positionPanel();
  }

  _positionPanel(){
    const trigger = this._getTriggerEl();
    if (!trigger) return;

    const r = trigger.getBoundingClientRect();
    const placement = this.placement;

    const margin = 8;
    const panel = this._panel;

    // Measure (needs display)
    panel.style.left = "-9999px";
    panel.style.top = "-9999px";

    const pr = panel.getBoundingClientRect();
    const w = pr.width || 420;
    const h = pr.height || 340;

    let left = r.left;
    let top = r.bottom + 8;

    const alignEnd = placement.endsWith("end");
    const alignTop = placement.startsWith("top");

    if (alignEnd) left = r.right - w;
    if (alignTop) top = r.top - h - 8;

    // Clamp to viewport
    left = Math.max(margin, Math.min(left, window.innerWidth - w - margin));
    top = Math.max(margin, Math.min(top, window.innerHeight - h - margin));

    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(top)}px`;
  }

  _computeUnread(){
    return this._notifications.reduce((acc, n) => acc + (n.read ? 0 : 1), 0);
  }

  _render(){
    const max = Math.max(1, Math.min(200, this.max));
    const list = this._notifications.slice(0, max);

    const unread = this._computeUnread();
    this.setAttribute("data-unread", String(unread));
    this._dot.textContent = unread > 99 ? "99+" : String(unread);

    // Header subtitle
    const total = this._notifications.length;
    this._sub.textContent = total ? `${unread} unread • ${total} total` : "No notifications";

    // Empty
    const empty = total === 0;
    this._empty.hidden = !empty;
    this._list.style.display = empty ? "none" : "grid";

    // Render list
    const html = list.map((n) => {
      const read = !!n.read;
      const tone = n.tone;

      const tonePill = tone !== "neutral"
        ? `<ctl-tag tone="${esc(tone)}">${esc(tone)}</ctl-tag>`
        : ``;

      const actions = (n.actions && n.actions.length)
        ? `<div class="pill" part="actions">
            ${n.actions.map((a) => `
              <ctl-button size="sm" variant="secondary"
                data-role="action"
                data-id="${esc(n.id)}"
                data-action="${esc(a.id)}">${esc(a.label)}</ctl-button>
            `).join("")}
          </div>`
        : ``;

      const meta = n.time ? `<span class="meta">${esc(n.time)}</span>` : `<span class="meta"></span>`;
      const unreadDot = read ? `` : `<span class="unreadMark" aria-hidden="true"></span>`;

      // data-href enables router intercept pattern (prevent default nav and emit event)
      const href = n.href ? `data-href="${esc(n.href)}"` : ``;

      return `
        <li>
          <div class="item"
               tabindex="0"
               role="option"
               aria-selected="${read ? "false" : "true"}"
               data-id="${esc(n.id)}"
               ${href}>
            <div class="itemTop">
              <div style="display:flex; align-items:flex-start; gap:10px; min-width:0;">
                ${unreadDot}
                <div style="min-width:0;">
                  <div class="itemTitle">${esc(n.title)}</div>
                  ${n.message ? `<div class="itemMsg">${esc(n.message)}</div>` : ``}
                </div>
              </div>
              ${meta}
            </div>

            <div class="row">
              <div class="pill">
                ${tonePill}
                ${n.href ? `<ctl-tag tone="info">link</ctl-tag>` : ``}
              </div>
              <ctl-button size="sm" variant="secondary" data-role="mark"
                data-id="${esc(n.id)}">${read ? "Mark unread" : "Mark read"}</ctl-button>
            </div>

            ${actions ? `<div style="margin-top:var(--ctl-space-1-5);">${actions}</div>` : ``}
          </div>
        </li>
      `;
    }).join("");

    this._list.innerHTML = html;

    // Reposition if open (size changed)
    if (this.open) queueMicrotask(() => this._positionPanel());
  }

  _onListClick(e){
    const path = e.composedPath ? e.composedPath() : [];
    const actionBtn = path.find((el) => el?.dataset?.role === "action");
    if (actionBtn) return this._onActionClick(e, actionBtn);

    const markBtn = path.find((el) => el?.dataset?.role === "mark");
    if (markBtn) {
      e.preventDefault();
      e.stopPropagation();
      const id = markBtn.dataset.id;
      const n = this._notifications.find((x) => x.id === id);
      if (!n) return;
      this.markRead(id, !n.read);
      return;
    }

    const itemEl = path.find((el) => el?.classList?.contains?.("item") && el?.dataset?.id);
    if (!itemEl) return;

    const id = itemEl.dataset.id;
    const notif = this._notifications.find((x) => x.id === id);
    if (!notif) return;

    // Clicking item marks it read (common enterprise behavior)
    if (!notif.read) this.markRead(id, true);

    // Router intercept
    const href = itemEl.dataset.href;
    if (href) {
      this.dispatchEvent(new CustomEvent("ctl-navigate", {
        bubbles: true,
        composed: true,
        detail: { id, href, notification: notif }
      }));
      // Leave panel open or close? Most apps close.
      this._close();
      return;
    }
  }

  _onActionClick(e, actionBtn){
    e.preventDefault();
    e.stopPropagation();

    const id = actionBtn.dataset.id;
    const actionId = actionBtn.dataset.action;
    const notif = this._notifications.find((x) => x.id === id);
    if (!notif) return;

    this.dispatchEvent(new CustomEvent("ctl-action", {
      bubbles: true,
      composed: true,
      detail: { id, actionId, notification: notif }
    }));
  }

  _onBtnCloseClick() {
    this._close();
  }

  _onBtnClearClick() {
    this.clearAll();
  }

  _onBtnMarkAllClick() {
    for (const n of this._notifications) {
      if (!n.read) this.markRead(n.id, true);
    }
  }
}

customElements.define("ctl-notification-center", CtlNotificationCenter);
